/**
 * useDocumentCreateDestination — orchestrates the destination + folder
 * state for a single ``document_create`` action proposal.
 *
 * Responsibilities (single-purpose by design):
 *   - Initial-value seeding from the agent's ``available_destinations``
 *     (cloud preferred → download → minio fallback). Matches the BE
 *     priority chain in
 *     ``src/services/action_service.py:create_document_create_proposal``.
 *   - Local controlled state for the user's pick (destination + folder).
 *   - Provider-scoped folder fetching via the existing
 *     ``useDriveFolders`` TanStack hook — folders are filtered to the
 *     currently-picked cloud provider; non-cloud destinations skip the
 *     network entirely (``enabled: false``).
 *   - A pre-built ``modifications`` payload the caller can spread into
 *     the execute mutation body, so the wire shape stays in lock-step
 *     with the BE without the consumer remembering field names.
 *
 * Architecture notes:
 *   - The presentational ``DestinationPicker`` is fully controlled — this
 *     hook owns the state on its behalf. Consumers only need to wire
 *     ``state`` + ``setValue`` → ``<DocumentCreatePreview destination={…}>``
 *     and read ``buildModifications()`` on submit.
 *   - Folders fall back to ``[]`` while fetching so the picker can render
 *     a skeleton without conditional branching at the call site.
 *   - The hook is intentionally Storybook-friendly via ``initialValue``
 *     and ``foldersOverride`` (test-only props on the consumer side; not
 *     part of the public API).
 */
import * as React from "react";
import {
  type DestinationChoice,
  type DestinationFolderItem,
  type SaveDestination,
  isCloudDestination,
} from "@ando/ui";
import type {
  DriveFolder,
  DriveFolderProvider,
} from "@ando/core/api";

import { useDriveFolders } from "../../chat/hooks/useDriveFolders";

const KNOWN_DESTINATIONS: ReadonlySet<SaveDestination> = new Set<SaveDestination>([
  "google_drive",
  "onedrive",
  "sharepoint",
  "download",
  "minio",
]);

/**
 * Pick the seed destination from the BE-emitted ``available_destinations``.
 * Preference order (June 2026 user feedback — "Archivio Ando è il default!"):
 *   1. ``minio`` — the internal "Archivio Ando" (Documenti). The user keeps
 *      every generated file here and opens it in-app; it never depends on an
 *      external connection working (a SharePoint/Drive push can fail). This is
 *      the safe, expected default.
 *   2. ``download`` — explicit user-action surface (presigned URL).
 *   3. Cloud (google_drive, onedrive, sharepoint) — chosen EXPLICITLY only
 *      when the user wants the file pushed to their cloud (never auto-selected,
 *      so a connected SharePoint doesn't silently capture every new file).
 */
function pickSeedDestination(
  available: ReadonlyArray<string>,
): SaveDestination {
  const allowlist = available.filter((d): d is SaveDestination =>
    KNOWN_DESTINATIONS.has(d as SaveDestination),
  );
  if (allowlist.includes("minio")) return "minio";
  if (allowlist.includes("download")) return "download";
  // ``isCloudDestination`` is the single source of truth for "what is a cloud
  // provider" — kept beside the picker so seed + UI logic never drift.
  const cloud = allowlist.find(isCloudDestination);
  if (cloud) return cloud;
  return "minio";
}

/**
 * Modifications payload mirroring the BE
 * ``ExecuteDocumentCreateRequest`` / generic ``modifications`` keys.
 * Only fields that differ from defaults are emitted, so the execute
 * audit log stores only the diff.
 */
export interface DocumentCreateDestinationModifications {
  readonly save_destination: SaveDestination;
  readonly folder_id?: string;
  readonly folder_name?: string;
}

export interface UseDocumentCreateDestinationOptions {
  /** ``available_destinations`` from the BE wire. */
  readonly availableDestinations: ReadonlyArray<string>;
}

export interface UseDocumentCreateDestinationResult {
  /** Current destination choice (controlled). */
  readonly value: DestinationChoice;
  /** Setter for the picker's ``onChange``. */
  readonly setValue: (next: DestinationChoice) => void;
  /** Folders filtered for the currently-picked cloud provider. */
  readonly folders: ReadonlyArray<DestinationFolderItem>;
  /** True while the folder list is being fetched. */
  readonly isLoadingFolders: boolean;
  /**
   * Build the modifications subset to spread into ``executeAction``'s
   * body. Only emits cloud-routing fields when relevant — keeps the
   * audit-log diff minimal.
   */
  readonly buildModifications: () => DocumentCreateDestinationModifications;
}

/**
 * Map ``DriveFolder`` (api wire) → ``DestinationFolderItem`` (ui prop).
 * Centralised so any future field-name drift on the BE only touches
 * this hook.
 */
function adaptFolder(f: DriveFolder): DestinationFolderItem {
  return {
    folderId: f.folder_id,
    name: f.name,
    path: f.path,
    provider: f.provider,
  };
}

export function useDocumentCreateDestination(
  opts: UseDocumentCreateDestinationOptions,
): UseDocumentCreateDestinationResult {
  const { availableDestinations } = opts;

  // Seed once per proposal — lazy ``useState`` initializer runs exactly
  // once on the first render, so subsequent prop changes to
  // ``availableDestinations`` never clobber a user pick. The BE never
  // mutates the list for the same proposal anyway, but the lazy
  // initializer is the canonical React idiom (no dep-array exemption
  // needed, no stale-closure foot-gun).
  const [value, setValue] = React.useState<DestinationChoice>(() => ({
    destination: pickSeedDestination(availableDestinations),
    folderId: null,
    folderName: null,
  }));

  // Only fetch folders when a cloud destination is picked. ``useDriveFolders``
  // is built on TanStack so it caches across mounts (5 min staleTime).
  // The ``enabled`` gate prevents the network call entirely for
  // download/minio.
  const provider: DriveFolderProvider | undefined = isCloudDestination(
    value.destination,
  )
    ? value.destination
    : undefined;
  const foldersQuery = useDriveFolders(provider ? { provider } : undefined);
  const enabled = provider !== undefined;

  const folders = React.useMemo<ReadonlyArray<DestinationFolderItem>>(() => {
    if (!enabled || !foldersQuery.data) return [];
    return foldersQuery.data.map(adaptFolder);
  }, [enabled, foldersQuery.data]);

  const buildModifications = React.useCallback(
    (): DocumentCreateDestinationModifications => {
      const out: DocumentCreateDestinationModifications = {
        save_destination: value.destination,
      };
      if (value.folderId) {
        // Only emit folder_id when we have one — undefined folder_id
        // means "let the BE auto-create the 'Ando Generated' folder
        // (Drive) or save to provider root (OneDrive/SharePoint)".
        return {
          ...out,
          folder_id: value.folderId,
          folder_name: value.folderName ?? undefined,
        };
      }
      return out;
    },
    [value],
  );

  return {
    value,
    setValue,
    folders,
    // ``isLoading`` covers both first-fetch + refetch-after-staleness;
    // either way the picker should skeleton until data lands.
    isLoadingFolders: enabled && foldersQuery.isLoading,
    buildModifications,
  };
}
